import { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  ButtonGroup,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  LinearProgress,
  Stack,
  IconButton,
} from '@mui/material';
import { Upload, Plus, Trash2, Info, Globe, FileText, AlertCircle } from 'lucide-react';
import FileList from '../../components/knowledgeBase/FileList';
import FileUpload from '../../components/knowledgeBase/FileUpload';
import { useFiles } from '../../hooks/file/useFiles';
import ConfirmationDialog from '../../components/common/ConfirmationDialog';
import Notifications from '../../components/common/Notifications';
import { NotificationT, SEVERITY } from '../../types/notification';
import { useAuth } from '../../hooks';
import { AUTH_CONSTANTS } from '../../config/constants';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { KnowledgeBaseUrls, KnowledgeBaseUrlItem } from '../../types/file';
import { orgIngestStatusLongPolling } from '../../components/knowledgeBase/IngestStatus';
import { organizationEndpoints } from '../../services/api/endpoints/organisation.endpoint';
import IngestStatusIndicator from '../../components/knowledgeBase/IngestStatusIndicator';

const KnowledgeBaseView = () => {
  const {
    files,
    knowledgeBaseUrls,
    knowledgeBaseUrlItems,
    downloadFile,
    downloading,
    isLoading,
    error,
    deleteFile,
    deleteKnowledgeBaseUrl,
    refetch,
    refetchUrls,
    createKnowledgeBaseUrls,
    checkCrawlStatus,
    ingestPolling,
    startIngestPolling,
    stopIngestPolling,
  } = useFiles();
  const { role } = useAuth();
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [urlModalOpen, setUrlModalOpen] = useState(false);
  const [urlInput, setUrlInput] = useState<string>('');
  const [urlError, setUrlError] = useState<string>('');
  const [pageLoading, setPageLoading] = useState(true);
  const [fileDeleting, setFileDeleting] = useState<string | null>(null);
  const [urlSaving, setUrlSaving] = useState(false);
  const [newUrl, setNewUrl] = useState('');

  const organizationId = useSelector(
    (state: RootState) => state.organizationContext.selectedOrganizationId
  );

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    fileId: '',
  });

  const [urlConfirmDialog, setUrlConfirmDialog] = useState({
    open: false,
    url: '',
  });

  const [urlDeleting, setUrlDeleting] = useState<string | null>(null);
  // Store UUID and URLs for tracking crawl status
  const [crawlUuid, setCrawlUuid] = useState<string | null>(null);
  const [crawlUrls, setCrawlUrls] = useState<string[]>([]);
  const [crawlStatus, setCrawlStatus] = useState<{
    status: 'STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED' | null;
    message: string;
    successfulPages?: number;
    failedPages?: number;
  } | null>(null);

  // Track crawling status per URL
  const [urlCrawlStatuses, setUrlCrawlStatuses] = useState<
    Record<
      string,
      {
        uuid: string;
        status: 'STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
        message: string;
        successfulPages?: number;
        failedPages?: number;
      }
    >
  >({});

  const [notification, setNotification] = useState<NotificationT>({
    open: false,
    message: '',
    severity: SEVERITY.SUCCESS,
  });

  // Organization ingest status (from public API)
  const [orgIngestStatus, setOrgIngestStatus] = useState<string | null>(null);
  const ingestPollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const isValidUrl = (url: string) => {
    try {
      const urlObj = new URL(url);
      if (urlObj.protocol !== 'https:') {
        return false;
      }

      const domainPattern = /^([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;
      return domainPattern.test(urlObj.hostname);
    } catch {
      return false;
    }
  };

  const handleAddUrl = () => {
    setUrlInput('');
    setUrlModalOpen(true);
  };

  const handleSaveUrl = async () => {
    const raw = urlInput
      .split(/\r?\n|,/)
      .map(s => s.trim().toLowerCase())
      .filter(Boolean);

    const invalid = raw.filter(u => !isValidUrl(u));
    if (invalid.length) {
      setUrlError(`Invalid HTTPS URLs (must be complete URLs with domain):\n${invalid.join('\n')}`);
      return;
    }

    setUrlSaving(true);
    setUrlError('');
    try {
      console.log('raw', raw);
      const data: KnowledgeBaseUrls = { clientId: organizationId || '', urls: raw };
      const response = await createKnowledgeBaseUrls(data);

      console.log('📥 API Response:', response);

      // Extract UUID from response - try both response.uuid and response.data.uuid
      const uuid = response.uuid || response.data?.uuid;

      // Store the UUID and URLs for status tracking and start polling
      if (uuid) {
        console.log('✅ UUID received, setting up polling:', uuid);
        setCrawlUuid(uuid);
        const urlsToTrack = response.data?.urls || response.urls || raw;
        setCrawlUrls(urlsToTrack); // Store the URLs that are being crawled

        const newCrawlStatus = {
          status: 'IN_PROGRESS' as const,
          message: response.data?.message || response.message || 'Crawl initiated',
          successfulPages: 0,
          failedPages: 0,
        };
        console.log('📊 Setting crawl status:', newCrawlStatus);
        setCrawlStatus(newCrawlStatus);

        // Initialize per-URL crawl statuses
        const initialStatuses: Record<string, any> = {};
        urlsToTrack.forEach((url: string) => {
          initialStatuses[url] = {
            uuid: uuid,
            status: 'IN_PROGRESS',
            message: response.data?.message || response.message || 'Crawling in progress...',
            successfulPages: 0,
            failedPages: 0,
          };
        });
        setUrlCrawlStatuses(prev => ({ ...prev, ...initialStatuses }));

        console.log('✅ Crawl initiated:', {
          uuid: uuid,
          status: response.data?.status || response.status,
          message: response.data?.message || response.message,
          urls: urlsToTrack,
          urlsToTrack,
          timestamp: response.data?.timestamp || response.timestamp,
        });
      } else {
        console.warn('⚠️ No UUID in response, polling will not start');
      }

      setUrlModalOpen(false);

      // Show success message
      const successMessage = response.message || `${raw.length} URL(s) added successfully`;
      setNotification({
        open: true,
        message: successMessage,
        severity: SEVERITY.SUCCESS,
      });
      await refetchUrls();
      await refetch();
    } catch {
      setNotification({
        open: true,
        message: 'Failed to add Knowledge Base URLs',
        severity: SEVERITY.ERROR,
      });
    } finally {
      setUrlSaving(false);
    }
  };

  const handleUploadComplete = useCallback(async () => {
    setIsUploadOpen(false);
    startIngestPolling();
    try {
      await orgIngestStatusLongPolling(organizationId!);
      setNotification({
        open: true,
        message: 'Files uploaded successfully',
        severity: SEVERITY.SUCCESS,
      });
      await refetch();
    } catch {
      setNotification({
        open: true,
        message: 'Failed to upload files',
        severity: SEVERITY.ERROR,
      });
    } finally {
      stopIngestPolling();
    }
  }, [organizationId, refetch]);

  const handleDeleteConfirm = async () => {
    setFileDeleting(confirmDialog.fileId);
    try {
      await deleteFile(confirmDialog.fileId);
      await refetch();
      setNotification({
        open: true,
        message: 'File deleted successfully',
        severity: SEVERITY.SUCCESS,
      });
    } catch {
      setNotification({
        open: true,
        message: 'Failed to delete file',
        severity: SEVERITY.ERROR,
      });
    } finally {
      setConfirmDialog({ open: false, fileId: '' });
      setFileDeleting(null);
    }
  };

  const handleDeleteUrlConfirm = async () => {
    setUrlDeleting(urlConfirmDialog.url);
    try {
      await deleteKnowledgeBaseUrl(urlConfirmDialog.url);
      await refetchUrls();
      setNotification({
        open: true,
        message: 'URL deleted successfully',
        severity: SEVERITY.SUCCESS,
      });
    } catch (error: any) {
      setNotification({
        open: true,
        message: error.message || 'Failed to delete URL',
        severity: SEVERITY.ERROR,
      });
    } finally {
      setUrlConfirmDialog({ open: false, url: '' });
      setUrlDeleting(null);
    }
  };

  const handleAdd = () => {
    const trimmed = newUrl.trim();
    if (!trimmed) return; // empty
    if (!isValidUrl(trimmed)) {
      // malformed
      setUrlError('Please enter a valid HTTPS URL');
      return;
    }

    // duplicate check
    const existing = urlInput.split('\n').map(u => u.trim().toLowerCase());

    if (existing.includes(trimmed.toLowerCase())) {
      setUrlError('This URL already exists'); // ⬅️ show under the box
      return;
    }

    // all good
    setUrlInput(urlInput ? `${urlInput}\n${trimmed}` : trimmed);
    setNewUrl('');
    setUrlError('');
  };

  const handleDelete = (toRemove: string) => {
    setUrlInput(prev =>
      prev
        .split('\n')
        .map(u => u.trim())
        .filter(u => u !== toRemove)
        .join('\n')
    );
  };

  // Check and restore crawl status for URLs with status_id
  const checkAndRestoreCrawlStatuses = useCallback(
    async (urlItems: KnowledgeBaseUrlItem[]) => {
      // Find URLs that have a status_id (indicating they may be crawling)
      const urlsWithStatusId = urlItems.filter(item => item.status_id);

      if (urlsWithStatusId.length === 0) return;

      console.log('🔄 Checking crawl status for URLs with status_id:', urlsWithStatusId);

      // Check crawl status for each URL with status_id
      for (const item of urlsWithStatusId) {
        try {
          const status = await checkCrawlStatus(item.status_id!);
          console.log(`📊 Crawl status for ${item.url}:`, status);

          if (status.currentStatus) {
            // If still in progress or started, restore the polling state
            const isActiveStatus =
              status.currentStatus.status === 'IN_PROGRESS' ||
              status.currentStatus.status === 'STARTED';
            if (isActiveStatus) {
              console.log(`🚀 Restoring polling for URL: ${item.url}`);

              // Set the crawl UUID and URLs to restore polling
              setCrawlUuid(item.status_id!);
              setCrawlUrls([item.url]);
              setCrawlStatus({
                status: status.currentStatus.status,
                message: status.currentStatus.message,
                successfulPages: status.currentStatus.successfulPages,
                failedPages: status.currentStatus.failedPages,
              });

              // Update per-URL status
              setUrlCrawlStatuses(prev => ({
                ...prev,
                [item.url]: {
                  uuid: item.status_id!,
                  status: status.currentStatus!.status,
                  message: status.currentStatus!.message,
                  successfulPages: status.currentStatus!.successfulPages,
                  failedPages: status.currentStatus!.failedPages,
                },
              }));
            }
          }
        } catch (err) {
          console.error(`❌ Failed to check crawl status for ${item.url}:`, err);
        }
      }
    },
    [checkCrawlStatus]
  );

  // Check and poll organization ingest status from public API
  const checkAndPollOrgIngestStatus = useCallback(async () => {
    if (!organizationId) return;

    try {
      const { ingestStatus } =
        await organizationEndpoints.getOrganisationIngestStatus(organizationId);
      console.log('📊 Organization ingest status:', ingestStatus);
      setOrgIngestStatus(ingestStatus);

      // If status is 'started', start polling every 5 seconds
      if (ingestStatus === 'started') {
        console.log('🚀 Starting organization ingest status polling...');

        // Clear any existing interval
        if (ingestPollIntervalRef.current) {
          clearInterval(ingestPollIntervalRef.current);
        }

        ingestPollIntervalRef.current = setInterval(async () => {
          try {
            const { ingestStatus: newStatus } =
              await organizationEndpoints.getOrganisationIngestStatus(organizationId);
            console.log('📡 Polling org ingest status:', newStatus);
            setOrgIngestStatus(newStatus);

            // If completed, stop polling and refresh data
            if (newStatus === 'completed' || newStatus !== 'started') {
              console.log('✅ Organization ingest completed, stopping polling');
              if (ingestPollIntervalRef.current) {
                clearInterval(ingestPollIntervalRef.current);
                ingestPollIntervalRef.current = null;
              }
              // Refresh files and URLs
              await Promise.all([refetch(), refetchUrls()]);
              setNotification({
                open: true,
                message: 'Files processed successfully',
                severity: SEVERITY.SUCCESS,
              });
            }
          } catch (err) {
            console.error('❌ Error polling org ingest status:', err);
          }
        }, 5000); // Poll every 5 seconds
      }
    } catch (err) {
      console.error('❌ Failed to check org ingest status:', err);
    }
  }, [organizationId, refetch, refetchUrls]);

  // Fetch data on mount and check for in-progress crawls
  useEffect(() => {
    let isMounted = true;

    // Reset ingestPolling on mount — it's transient state that shouldn't
    // survive page refreshes (Redux Persist may have kept it as true).
    stopIngestPolling();

    const fetchData = async () => {
      try {
        const [, urlItems] = await Promise.all([refetch(), refetchUrls()]);
        console.log('📦 Fetched URL items:', urlItems);
        // Check and restore crawl statuses for URLs that may be in progress
        if (isMounted && urlItems && urlItems.length > 0) {
          console.log('🔍 Checking for in-progress crawls...');
          await checkAndRestoreCrawlStatuses(urlItems);
        } else {
          console.log('⚠️ No URL items to check:', { isMounted, urlItems });
        }

        // Check organization ingest status from public API
        if (isMounted) {
          await checkAndPollOrgIngestStatus();
        }
      } catch (err) {
        console.error('Error loading initial data:', err);
      } finally {
        if (isMounted) {
          setPageLoading(false);
        }
      }
    };

    fetchData();

    // Cleanup function - runs when component unmounts
    return () => {
      isMounted = false;
      // Clear ingest polling interval on unmount
      if (ingestPollIntervalRef.current) {
        clearInterval(ingestPollIntervalRef.current);
        ingestPollIntervalRef.current = null;
      }
    };
    // Empty dependency array - only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ref to track polling interval
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Poll crawl status when UUID is available and status is IN_PROGRESS or STARTED
  useEffect(() => {
    const isPollingStatus =
      crawlStatus?.status === 'IN_PROGRESS' || crawlStatus?.status === 'STARTED';

    // If not in a polling state, clear any existing interval and return
    if (!crawlUuid || !crawlStatus || !isPollingStatus) {
      if (pollIntervalRef.current) {
        console.log('⏹️ Clearing polling interval');
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      return;
    }

    // If already polling, don't start another interval
    if (pollIntervalRef.current) {
      console.log('⏸️ Polling already active, skipping...');
      return;
    }

    console.log('🚀 Starting crawl status polling for UUID:', crawlUuid);

    // Poll function
    const pollStatus = async () => {
      try {
        console.log('📡 Polling crawl status for UUID:', crawlUuid);
        const status = await checkCrawlStatus(crawlUuid);

        console.log('📥 Poll response:', status);

        if (status.currentStatus) {
          // Update crawl status
          setCrawlStatus({
            status: status.currentStatus.status,
            message: status.currentStatus.message,
            successfulPages: status.currentStatus.successfulPages,
            failedPages: status.currentStatus.failedPages,
          });

          // Update per-URL statuses
          setUrlCrawlStatuses(prev => {
            const updated = { ...prev };
            crawlUrls.forEach((url: string) => {
              updated[url] = {
                uuid: crawlUuid,
                status: status.currentStatus!.status,
                message: status.currentStatus!.message,
                successfulPages: status.currentStatus!.successfulPages,
                failedPages: status.currentStatus!.failedPages,
              };
            });
            return updated;
          });

          // If completed, stop polling and show success notification
          if (status.currentStatus.status === 'COMPLETED') {
            console.log('✅ Crawl completed, stopping polling');
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }
            const pagesCount = status.currentStatus.successfulPages ?? 0;
            setNotification({
              open: true,
              message: `Crawl completed! ${pagesCount} pages crawled successfully.`,
              severity: SEVERITY.SUCCESS,
            });
            await refetch();
            await refetchUrls();

            // Clear crawl state after successful completion
            setCrawlStatus(null);
            setCrawlUuid(null);
            setCrawlUrls([]);

            // Clear per-URL statuses after delay
            setTimeout(() => {
              const clearStatuses: Record<string, any> = {};
              crawlUrls.forEach((url: string) => {
                clearStatuses[url] = undefined;
              });
              setUrlCrawlStatuses(prev => {
                const updated = { ...prev };
                Object.keys(clearStatuses).forEach(url => delete updated[url]);
                return updated;
              });
            }, 3000);
          }
          // If failed, stop polling, delete the URLs, and show error notification
          else if (status.currentStatus.status === 'FAILED') {
            console.log('❌ Crawl failed, stopping polling and deleting URLs');
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }

            // Automatically delete the failed URLs
            console.log('❌ Crawl failed, deleting URLs:', crawlUrls);
            const deletePromises = crawlUrls.map(url =>
              deleteKnowledgeBaseUrl(url).catch(err => {
                console.error(`Failed to delete URL ${url}:`, err);
              })
            );

            await Promise.all(deletePromises);
            await refetchUrls();

            // Clear per-URL statuses for deleted URLs
            setUrlCrawlStatuses(prev => {
              const updated = { ...prev };
              crawlUrls.forEach(url => delete updated[url]);
              return updated;
            });

            // Clear crawl state
            setCrawlStatus(null);
            setCrawlUuid(null);
            setCrawlUrls([]);

            setNotification({
              open: true,
              message: `Upload failed: ${status.currentStatus.message}. URLs have been removed.`,
              severity: SEVERITY.ERROR,
            });
          }
        }
      } catch (error: any) {
        console.error('❌ Polling error:', {
          error,
          message: error.message,
          status: error.response?.status,
        });
      }
    };

    // Poll immediately, then every 5 seconds
    pollStatus();
    pollIntervalRef.current = setInterval(pollStatus, 5000);

    // Cleanup on unmount
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
    // Only re-run when crawlUuid changes (not on every status update)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crawlUuid]);

  if (pageLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  console.log('🔍 Current state:', {
    isLoading,
    knowledgeBaseUrls,
    urlCrawlStatuses,
    crawlUuid,
    crawlStatus,
    orgIngestStatus,
  });

  return (
    <Box className="knowledge-base-tour">
      {/* Organization ingest status indicator - shows when files are being processed */}
      {orgIngestStatus === 'started' && <IngestStatusIndicator message="Processing files..." />}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          px: 2,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 500 }}>
          Knowledge Base
        </Typography>
      </Box>

      <Paper elevation={0} sx={{ p: 3, borderRadius: 2 }}>
        {/* Non-blocking crawl progress indicator */}
        {crawlStatus &&
          (crawlStatus.status === 'IN_PROGRESS' || crawlStatus.status === 'STARTED') && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Box sx={{ width: '100%' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1.5,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {crawlStatus.message}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                    {crawlStatus.successfulPages ?? 0} pages crawled
                  </Typography>
                </Box>
                <LinearProgress
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 3,
                    },
                  }}
                />
              </Box>
            </Alert>
          )}

        {/* Failed crawl indicator */}
        {crawlStatus && crawlStatus.status === 'FAILED' && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            onClose={() => {
              setCrawlStatus(null);
              setCrawlUuid(null);
              setCrawlUrls([]);
            }}
          >
            <Box sx={{ width: '100%' }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Upload failed: {crawlStatus.message}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                The URLs have been automatically removed. Please try again or upload files manually.
              </Typography>
            </Box>
          </Alert>
        )}

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="h6">
            Upload Content{' '}
            <Typography variant="body2" color="text.secondary">
              Upload Files and URLs and manage your documents
            </Typography>
          </Typography>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {/* 2. Centre-aligned “Add URL” button */}
            {!urlSaving && !ingestPolling && role !== AUTH_CONSTANTS.ROLES.USER && (
              <Button startIcon={<Plus size={16} />} variant="contained" onClick={handleAddUrl}>
                Add URL
              </Button>
            )}
            {!ingestPolling && role !== AUTH_CONSTANTS.ROLES.USER ? (
              <ButtonGroup variant="contained" size="small">
                <Button startIcon={<Upload size={16} />} onClick={() => setIsUploadOpen(true)}>
                  Upload Files
                </Button>
              </ButtonGroup>
            ) : null}
          </Box>
        </Box>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <FileList
          files={files}
          onDelete={id => setConfirmDialog({ open: true, fileId: id })}
          deletingFileId={fileDeleting}
          onDownload={fileName => downloadFile(fileName)}
          downloading={downloading}
        />

        {/* Knowledge Base URLs Section */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Knowledge Base URLs
          </Typography>

          {/* Info Panel */}
          <Paper
            elevation={0}
            sx={{
              mb: 2,
              p: 2.5,
              bgcolor: theme =>
                theme.palette.mode === 'dark' ? 'rgba(43, 23, 172, 0.08)' : 'primary.50',
              border: '1px solid',
              borderColor: theme =>
                theme.palette.mode === 'dark' ? 'rgba(43, 23, 172, 0.2)' : 'primary.100',
              borderRadius: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
              <Box sx={{ color: 'primary.main', display: 'flex', mt: '2px' }}>
                <Info size={20} />
              </Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                How URL Crawling Works
              </Typography>
            </Box>

            <Stack spacing={1.5}>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <Box sx={{ color: 'primary.main', display: 'flex', mt: '3px' }}>
                  <Globe size={16} />
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                  URLs are crawled and converted to text files for your knowledge base
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <Box sx={{ color: 'primary.main', display: 'flex', mt: '3px' }}>
                  <FileText size={16} />
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                  Crawler uses <strong>sitemap.xml</strong> to discover pages (required)
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <Box sx={{ color: 'warning.main', display: 'flex', mt: '3px' }}>
                  <AlertCircle size={16} />
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                  <strong>Limitations:</strong> Maximum 25 pages per URL. JavaScript-rendered
                  websites (SPAs) cannot be crawled
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <Box sx={{ color: 'primary.main', display: 'flex', mt: '3px' }}>
                  <Upload size={16} />
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                  <strong>Alternative:</strong> Upload text files manually if crawling fails
                </Typography>
              </Box>
            </Stack>
          </Paper>

          <Paper
            variant="outlined"
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            {knowledgeBaseUrls && knowledgeBaseUrls.length > 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0,
                }}
              >
                {knowledgeBaseUrls.map((url, index) => {
                  const urlStatus = urlCrawlStatuses[url];
                  const isCrawling =
                    urlStatus?.status === 'IN_PROGRESS' || urlStatus?.status === 'STARTED';
                  const isCompleted = urlStatus?.status === 'COMPLETED';

                  return (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        p: 2.5,
                        borderBottom: index < knowledgeBaseUrls.length - 1 ? '1px solid' : 'none',
                        borderColor: 'divider',
                        bgcolor: urlDeleting === url ? 'action.hover' : 'background.paper',
                        transition: 'all 0.2s',
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
                          <Box
                            sx={{
                              color: isCrawling ? 'info.main' : 'primary.main',
                              display: 'flex',
                            }}
                          >
                            {isCrawling ? (
                              <CircularProgress size={18} color="info" />
                            ) : (
                              <Globe size={18} />
                            )}
                          </Box>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                wordBreak: 'break-all',
                                color: 'text.primary',
                                fontWeight: 500,
                              }}
                            >
                              {url}
                            </Typography>
                            {isCrawling && (
                              <Typography
                                variant="caption"
                                color="info.main"
                                sx={{ mt: 0.5, display: 'block' }}
                              >
                                {urlStatus.successfulPages !== undefined &&
                                urlStatus.successfulPages > 0
                                  ? `${urlStatus.successfulPages} pages crawled...`
                                  : urlStatus.message || 'Crawling in progress...'}
                              </Typography>
                            )}
                            {isCompleted && (
                              <Typography
                                variant="caption"
                                color="success.main"
                                sx={{ mt: 0.5, display: 'block' }}
                              >
                                ✓ Completed
                                {urlStatus.successfulPages !== undefined
                                  ? ` - ${urlStatus.successfulPages} pages`
                                  : ''}
                              </Typography>
                            )}
                          </Box>
                        </Box>

                        {role !== AUTH_CONSTANTS.ROLES.USER && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setUrlConfirmDialog({ open: true, url })}
                            disabled={urlDeleting === url || isCrawling}
                            aria-label="Delete URL"
                            sx={{
                              '&:hover': {
                                bgcolor: 'error.lighter',
                              },
                            }}
                          >
                            {urlDeleting === url ? (
                              <CircularProgress size={16} />
                            ) : (
                              <Trash2 size={16} />
                            )}
                          </IconButton>
                        )}
                      </Box>

                      {/* Progress bar for crawling URLs */}
                      {isCrawling && (
                        <Box sx={{ mt: 1.5 }}>
                          <LinearProgress
                            sx={{
                              height: 4,
                              borderRadius: 2,
                              backgroundColor: theme =>
                                theme.palette.mode === 'dark'
                                  ? 'rgba(255, 255, 255, 0.1)'
                                  : 'rgba(0, 0, 0, 0.08)',
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 2,
                              },
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 8,
                  px: 3,
                }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    bgcolor: theme =>
                      theme.palette.mode === 'dark' ? 'rgba(43, 23, 172, 0.15)' : 'primary.50',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                    color: 'primary.main',
                  }}
                >
                  <Globe size={32} />
                </Box>
                <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600 }}>
                  No URLs Added
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center">
                  Click "Add URL" button to add website URLs to your knowledge base
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>

        {/* Dialogs and Modals */}
        <Dialog open={urlModalOpen} onClose={() => setUrlModalOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>Add Knowledge Base URL</DialogTitle>
          <DialogContent>
            <Stack spacing={1} sx={{ mb: 2, mt: 1 }}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <Box sx={{ flexGrow: 1 }}>
                  <TextField
                    autoFocus
                    size="small"
                    label="Add URL"
                    placeholder="https://example.com"
                    value={newUrl}
                    onChange={e => {
                      const val = e.target.value;
                      setNewUrl(val);

                      // live validation
                      if (val.trim() && !isValidUrl(val.trim())) {
                        setUrlError('Please enter a valid HTTPS URL');
                      } else {
                        setUrlError('');
                      }
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (isValidUrl(newUrl.trim())) handleAdd();
                      }
                    }}
                    error={!!urlError}
                    helperText={urlError}
                    fullWidth
                  />
                </Box>
                {newUrl.trim() && (
                  <Button
                    variant="contained"
                    disabled={!isValidUrl(newUrl.trim())}
                    onClick={handleAdd}
                    sx={{ height: '40px' }}
                  >
                    Add
                  </Button>
                )}
              </Stack>

              {urlInput && (
                <Stack spacing={1} sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                    Added URLs
                  </Typography>

                  {urlInput
                    .split('\n')
                    .map(u => u.trim())
                    .filter(Boolean)
                    .map(u => (
                      <Paper
                        key={u}
                        variant="outlined"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          px: 2,
                          py: 1,
                          borderRadius: 1,
                          bgcolor: 'action.hover',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            flexGrow: 1,
                          }}
                        >
                          {u}
                        </Typography>

                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(u)}
                          aria-label="Remove URL"
                        >
                          <Trash2 size={16} />
                        </IconButton>
                      </Paper>
                    ))}
                </Stack>
              )}
            </Stack>
            <Box mt={2}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: theme =>
                    theme.palette.mode === 'dark' ? 'rgba(43, 23, 172, 0.08)' : 'primary.50',
                  border: '1px solid',
                  borderColor: theme =>
                    theme.palette.mode === 'dark' ? 'rgba(43, 23, 172, 0.2)' : 'primary.100',
                  borderRadius: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Box sx={{ color: 'primary.main', display: 'flex' }}>
                    <Info size={18} />
                  </Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    Important Information
                  </Typography>
                </Box>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    <Box sx={{ color: 'text.secondary', display: 'flex', mt: '2px' }}>•</Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Maximum <strong>25 pages per URL</strong>
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    <Box sx={{ color: 'text.secondary', display: 'flex', mt: '2px' }}>•</Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Requires a valid <strong>sitemap.xml</strong> file
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    <Box sx={{ color: 'text.secondary', display: 'flex', mt: '2px' }}>•</Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      <strong>JavaScript-rendered websites</strong> (SPAs) cannot be crawled
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    <Box sx={{ color: 'text.secondary', display: 'flex', mt: '2px' }}>•</Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      If crawling fails, upload content as a text file instead
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  If you need more URLs to crawl, more pages, or have any issues while crawling,
                  send an email to <strong>contact@cacxi.com</strong> to get it done.
                </Typography>
              </Alert>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUrlModalOpen(false)} disabled={urlSaving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveUrl}
              disabled={urlSaving || urlInput.trim().length === 0}
              startIcon={urlSaving ? <CircularProgress size={16} /> : undefined}
            >
              {urlSaving ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        <FileUpload
          open={isUploadOpen}
          onClose={() => setIsUploadOpen(false)}
          onUpload={handleUploadComplete}
        />

        <ConfirmationDialog
          open={confirmDialog.open}
          title="Delete File"
          message="Are you sure you want to delete this file? This action cannot be undone."
          onConfirm={handleDeleteConfirm}
          onCancel={() => setConfirmDialog({ open: false, fileId: '' })}
          isLoading={!!isLoading}
        />

        <ConfirmationDialog
          open={urlConfirmDialog.open}
          title="Delete URL"
          message={`Are you sure you want to delete this URL? This action cannot be undone.\n\n${urlConfirmDialog.url}`}
          onConfirm={handleDeleteUrlConfirm}
          onCancel={() => setUrlConfirmDialog({ open: false, url: '' })}
          isLoading={!!isLoading}
        />

        <Notifications
          open={notification.open}
          message={notification.message}
          severity={notification.severity}
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        />
      </Paper>
    </Box>
  );
};

export default KnowledgeBaseView;
